<template>
  <footer class="site-footer" :dir="isArabic ? 'rtl' : 'ltr'">
    <div class="footer-glow footer-glow--one" aria-hidden="true"></div>
    <div class="footer-glow footer-glow--two" aria-hidden="true"></div>

    <div class="footer-shell footer-main">
      <section class="brand-column" :aria-label="t.brand">
        <a class="footer-brand" :href="mainSiteUrl">
          <img src="@/assets/Splendapp-web.svg" :alt="t.brand">
        </a>
        <p>{{ t.description }}</p>
        <div class="trust-list" aria-label="t.benefits">
          <span>{{ t.free }}</span>
          <span>{{ t.private }}</span>
          <span>{{ t.noLogin }}</span>
        </div>
        <a class="footer-cta" :href="mainSiteUrl" @click="trackWebsiteClick">{{ t.websiteCta }} <span aria-hidden="true">{{ isArabic ? '←' : '→' }}</span></a>
      </section>

      <nav class="link-column" :aria-label="t.imageTools">
        <h2>{{ t.imageTools }}</h2>
        <ul>
          <li><router-link :to="categoryLink('image-editing')">{{ t.imageEditing }}</router-link></li>
          <li><router-link :to="categoryLink('image-optimization')">{{ t.imageOptimization }}</router-link></li>
          <li><router-link :to="categoryLink('image-creation')">{{ t.imageCreation }}</router-link></li>
          <li><router-link :to="categoryLink('privacy-effects')">{{ t.imagePrivacy }}</router-link></li>
        </ul>
      </nav>

      <nav class="link-column" :aria-label="t.popularTools">
        <h2>{{ t.popularTools }}</h2>
        <ul>
          <li><router-link :to="localizedRoute('image-compress')">{{ t.compressor }}</router-link></li>
          <li><router-link :to="localizedRoute('background-removal')">{{ t.backgroundRemover }}</router-link></li>
          <li><router-link :to="localizedRoute('image-editor')">{{ t.imageEditor }}</router-link></li>
          <li><router-link :to="localizedRoute('qr-generator')">{{ t.qrGenerator }}</router-link></li>
          <li><router-link :to="localizedRoute('schema-generator')">{{ t.schemaGenerator }}</router-link></li>
        </ul>
      </nav>

      <nav class="link-column" :aria-label="t.more">
        <h2>{{ t.more }}</h2>
        <ul>
          <li><router-link :to="categoryLink('qr-barcode')">{{ t.qrBarcode }}</router-link></li>
          <li><router-link :to="categoryLink('seo')">{{ t.seoTools }}</router-link></li>
          <li><router-link :to="categoryLink('business')">{{ t.businessTools }}</router-link></li>
          <li><router-link :to="{ name: 'home' }">{{ t.allTools }}</router-link></li>
          <li><a :href="mainSiteUrl" target="_blank" rel="noopener noreferrer">{{ t.splendappSite }}</a></li>
        </ul>
      </nav>
    </div>

    <div class="footer-bottom">
      <div class="footer-shell bottom-row">
        <p>© {{ currentYear }} SplendApp. {{ t.rights }}</p>
        <div class="bottom-links">
          <a href="mailto:info@splendapp.com">info@splendapp.com</a>
          <a :href="mainSiteUrl" target="_blank" rel="noopener noreferrer">splendapp.com</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { trackPromotionClick } from '@/utils/promotionAnalytics'

const COPY = {
  en: {
    brand: 'SplendApp Tools',
    description: 'Free browser-based tools for editing images, creating content, working with QR codes and improving technical SEO.',
    benefits: 'Tool benefits', free: 'Free tools', private: 'Browser-based', noLogin: 'No login',
    imageTools: 'Image tools', imageEditing: 'Image Editing', imageOptimization: 'Image Optimization', imageCreation: 'Image Creation', imagePrivacy: 'Image Privacy / Effects',
    popularTools: 'Popular tools', compressor: 'Image Compressor', backgroundRemover: 'Background Remover', imageEditor: 'Image Editor', qrGenerator: 'QR Code Generator', schemaGenerator: 'Schema Markup Generator',
    more: 'Explore', qrBarcode: 'QR & Barcode', seoTools: 'SEO Tools', businessTools: 'Business Tools', allTools: 'All Tools', splendappSite: 'SplendApp Website',
    rights: 'All rights reserved.', websiteCta: 'Build Your Mobile App with SplendApp'
  },
  ar: {
    brand: 'أدوات سبلينداب',
    description: 'أدوات مجانية تعمل داخل المتصفح لتعديل الصور وإنشاء المحتوى والتعامل مع رموز QR وتحسين SEO التقني.',
    benefits: 'مزايا الأدوات', free: 'أدوات مجانية', private: 'تعمل داخل المتصفح', noLogin: 'بدون تسجيل',
    imageTools: 'أدوات الصور', imageEditing: 'تعديل الصور', imageOptimization: 'تحسين الصور', imageCreation: 'إنشاء الصور', imagePrivacy: 'خصوصية وتأثيرات الصور',
    popularTools: 'أدوات شائعة', compressor: 'ضغط الصور', backgroundRemover: 'إزالة خلفية الصورة', imageEditor: 'محرر الصور', qrGenerator: 'مولد QR', schemaGenerator: 'مولد Schema Markup',
    more: 'استكشف', qrBarcode: 'QR والباركود', seoTools: 'أدوات تحسين محركات البحث', businessTools: 'أدوات الأعمال', allTools: 'كل الأدوات', splendappSite: 'موقع سبلينداب',
    rights: 'جميع الحقوق محفوظة.', websiteCta: 'أنشئ تطبيقك مع سبلينداب'
  }
}

export default {
  name: 'Footer2',
  computed: {
    isArabic() { return this.$store.state.currentLanguage === 'ar' },
    language() { return this.isArabic ? 'ar' : 'en' },
    t() { return COPY[this.language] },
    currentYear() { return new Date().getFullYear() },
    mainSiteUrl() { return this.isArabic ? 'https://splendapp.com' : 'https://splendapp.com/en/' }
  },
  methods: {
    categoryLink(category) {
      return { name: 'home', query: { category }, hash: '#tools' }
    },
    localizedRoute(baseName) {
      return { name: `${baseName}-${this.language}` }
    },
    trackWebsiteClick() {
      trackPromotionClick(this.$gtag, 'footer_website_cta', {
        language: this.language,
        source_route: this.$route.name || 'unknown'
      })
    }
  }
}
</script>

<style scoped>
.site-footer {
  position: relative;
  overflow: hidden;
  padding: 0;
  background: linear-gradient(135deg, #111d35 0%, #172b4d 52%, #203c70 100%);
  color: #cbd8ec;
  font-family: "Cairo", sans-serif;
  text-align: start;
}
.footer-glow { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(8px); opacity: .24; }
.footer-glow--one { width: 360px; height: 360px; inset-block-start: -220px; inset-inline-start: -100px; background: #3a7afe; }
.footer-glow--two { width: 300px; height: 300px; inset-block-end: -230px; inset-inline-end: 5%; background: #7aa3ee; }
.footer-shell { position: relative; width: min(1180px, calc(100% - 40px)); margin-inline: auto; }
.footer-main { display: grid; grid-template-columns: minmax(260px, 1.65fr) repeat(3, minmax(150px, 1fr)); gap: clamp(30px, 4vw, 64px); padding-block: 68px 56px; }
.footer-brand { display: inline-flex; padding: 11px 14px; border-radius: 12px; background: #fff; text-decoration: none; }
.footer-brand img { display: block; width: 168px; height: auto; }
.brand-column p { max-width: 390px; margin: 18px 0 22px; color: #b8c8df; font-size: .96rem; line-height: 1.85; }
.trust-list { display: flex; flex-wrap: wrap; gap: 8px; }
.trust-list span { padding: 6px 10px; border: 1px solid rgba(255,255,255,.14); border-radius: 999px; background: rgba(255,255,255,.06); color: #e8f1ff; font-size: .75rem; font-weight: 800; }
.footer-cta { display: inline-flex; align-items: center; gap: 9px; margin-top: 22px; padding: 11px 17px; border-radius: 999px; background: #3a7afe; color: #fff; font-size: .86rem; font-weight: 900; text-decoration: none; box-shadow: 0 10px 22px rgba(0,0,0,.18); }
.footer-cta:hover { background: #5b8dff; }
.link-column h2 { margin: 5px 0 20px; color: #fff; font-size: 1rem; font-weight: 900; }
.link-column ul { display: grid; gap: 12px; margin: 0; padding: 0; list-style: none; }
.link-column a { color: #b8c8df; font-size: .91rem; font-weight: 600; text-decoration: none; transition: color .2s ease, transform .2s ease; }
.link-column a:hover { color: #fff; }
.link-column a:focus-visible, .footer-brand:focus-visible, .footer-cta:focus-visible, .bottom-links a:focus-visible { outline: 3px solid #f59e0b; outline-offset: 4px; border-radius: 3px; }
.footer-bottom { position: relative; border-top: 1px solid rgba(255,255,255,.1); background: rgba(7, 16, 32, .28); }
.bottom-row { display: flex; align-items: center; justify-content: space-between; gap: 24px; min-height: 74px; padding-block: 18px; }
.bottom-row p { margin: 0; color: #93a8c7; font-size: .84rem; }
.bottom-links { display: flex; flex-wrap: wrap; gap: 22px; }
.bottom-links a { color: #cbd8ec; font-size: .84rem; font-weight: 700; text-decoration: none; }
.bottom-links a:hover { color: #fff; }
@media (max-width: 920px) {
  .footer-main { grid-template-columns: 1.4fr 1fr 1fr; }
  .brand-column { grid-column: 1 / -1; }
  .brand-column p { max-width: 620px; }
}
@media (max-width: 650px) {
  .footer-shell { width: min(100% - 32px, 1180px); }
  .footer-main { grid-template-columns: 1fr 1fr; gap: 36px 24px; padding-block: 48px 40px; }
  .brand-column { grid-column: 1 / -1; }
  .bottom-row { align-items: flex-start; flex-direction: column; gap: 12px; }
}
@media (max-width: 430px) {
  .footer-main { grid-template-columns: 1fr; }
  .brand-column { grid-column: auto; }
}
</style>
